import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from './protected-route';
import { LegacyGitSettingsRedirect } from './LegacyGitSettingsRedirect';
import { onboardingRoutes } from './onboarding';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { SettingsChrome } from '@/components/layout/SettingsChrome';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { InternalAdminGuard } from '@/features/sorcery/InternalAdminGuard';
import { NextShellSettingsGate } from '@/features/settings/NextShellSettingsGate';

// Lazy load pages for better performance
// DC-443 regression anchors — work-items browse surfaces: classic `/work-items`, My Work `/my-work/work-items`, Develop `/develop/work-items/:phase`
const Dashboard = lazy(() => import('@/pages/dashboard'));
const WorkItemsPage = lazy(() => import('@/pages/dashboard/WorkItemsPage'));
const WorkItemDetailPage = lazy(() => import('@/features/work-items/pages/WorkItemDetailPage'));
const RepositoryDashboard = lazy(() => import('@/pages/RepositoryDashboardPage'));
const InboxPage = lazy(() => import('@/features/inbox/pages/InboxPage'));
const LeaderboardPage = lazy(() => import('@/pages/dashboard/LeaderboardPage'));
const RequirementsDashboard = lazy(() => import('@/pages/dashboards/requirements'));
const AuthorDetailPage = lazy(() => import('@/pages/dashboards/requirements/AuthorDetail'));
const EngineerDirectory = lazy(() =>
  import('@/pages/engineers/EngineerDirectory').then((m) => ({
    default: m.EngineerDirectory,
  }))
);
const EngineerReportCard = lazy(() =>
  import('@/pages/engineers/EngineerReportCard').then((m) => ({
    default: m.EngineerReportCard,
  }))
);
const PullRequestHistoryPage = lazy(
  () => import('@/features/pull-requests/pages/PullRequestHistoryPage')
);
const PrDetailView = lazy(() => import('@/pages/dashboard/PrDetailView'));
const Login = lazy(() => import('@/pages/login'));
const SignInPage = lazy(() => import('@/pages/SignInPage'));
const McpAuthorizePage = lazy(() => import('@/pages/mcp/McpAuthorizePage'));
const SignUpPage = lazy(() => import('@/pages/SignUpPage'));
const RequestAccessPage = lazy(() => import('@/pages/RequestAccessPage'));
const ChooseOrganizationPage = lazy(() => import('@/pages/ChooseOrganizationPage'));
const InvitationLinkPage = lazy(() => import('@/pages/InvitationLinkPage'));
const AzureDevOpsConsentCompletePage = lazy(
  () => import('@/pages/connect/AzureDevOpsConsentCompletePage')
);
const Settings = lazy(() => import('@/pages/settings'));
const GitHubAppSuccess = lazy(() => import('@/pages/settings/github/GitHubAppSuccess'));
const GitHubAppError = lazy(() => import('@/pages/settings/github/GitHubAppError'));
const GitIntegrationsPage = lazy(() => import('@/pages/settings/git/GitIntegrationsPage'));
const MemberMcpIntegrationPage = lazy(
  () => import('@/pages/settings/mcp/MemberMcpIntegrationPage')
);
const RepositoriesSettingsPage = lazy(() =>
  import('@/pages/settings/RepositoriesSettingsPage').then((m) => ({
    default: m.RepositoriesSettingsPage,
  }))
);
const ReviewStandardsPage = lazy(() => import('@/pages/settings/ReviewStandardsPage'));
const WorkItemSettingsPage = lazy(() => import('@/pages/settings/WorkItemSettingsPage'));
const ProviderConnectionsSettingsPage = lazy(
  () => import('@/features/settings/provider-connections/ProviderConnectionsSummaryPage')
);
const TriggerPolicySettingsPage = lazy(
  () => import('@/features/settings/trigger-policy/TriggerPolicySettingsPage')
);
const ContextFreshnessPolicySettingsPage = lazy(
  () => import('@/features/settings/context-freshness-policy/ContextFreshnessPolicySettingsPage')
);
const SddGenerationGatePolicySettingsPage = lazy(
  () => import('@/features/settings/sdd-generation-gate-policy/SddGenerationGatePolicySettingsPage')
);
const CitationQualitySettingsPage = lazy(
  () => import('@/features/settings/citation-quality/CitationQualitySettingsPage')
);
const OrganizationSettingsPage = lazy(() => import('@/pages/settings/OrganizationSettingsPage'));
const SettingsApiKeys = lazy(() => import('@/pages/settings/api-keys'));
const SettingsBilling = lazy(() => import('@/pages/settings/billing'));
const SettingsUsage = lazy(() => import('@/pages/settings/usage'));
const SettingsApiGuide = lazy(() => import('@/pages/settings/api-guide'));
const SettingsOnboardingPage = lazy(() => import('@/pages/settings/SettingsOnboardingPage'));
const OrganizationMembers = lazy(() =>
  import('@/pages/OrganizationMembers').then((m) => ({
    default: m.OrganizationMembers,
  }))
);
const TeamsPage = lazy(() =>
  import('@/pages/teams/TeamsPage').then((m) => ({
    default: m.TeamsPage,
  }))
);
const TeamDashboardPage = lazy(() => import('@/pages/teams/TeamDashboardPage'));
const TeamsAggregateDashboard = lazy(() =>
  import('@/pages/teams/TeamsAggregateDashboard').then((m) => ({
    default: m.TeamsAggregateDashboard,
  }))
);
const RepositoriesAggregateDashboard = lazy(() =>
  import('@/pages/repositories/RepositoriesAggregateDashboard').then((m) => ({
    default: m.RepositoriesAggregateDashboard,
  }))
);
const AuditLogsPage = lazy(() =>
  import('@/pages/AuditLogsPage').then((m) => ({
    default: m.AuditLogsPage,
  }))
);
const UserNotifications = lazy(() =>
  import('@/pages/UserNotifications').then((m) => ({
    default: m.UserNotifications,
  }))
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({
    default: m.ProfilePage,
  }))
);
const StyleGuide = lazy(() => import('@/pages/StyleGuide'));
const NotFound = lazy(() => import('@/pages/not-found'));
const AccountSuspendedPage = lazy(() => import('@/pages/account-suspended'));
const SorceryTenantsPage = lazy(() => import('@/pages/sorcery/SorceryTenantsPage'));
const SorceryTenantDetailPage = lazy(() => import('@/pages/sorcery/SorceryTenantDetailPage'));
const SorceryTenantFeatureFlagsPage = lazy(
  () => import('@/pages/sorcery/SorceryTenantFeatureFlagsPage')
);
const SorceryTenantFeatureFlagHistoryPage = lazy(
  () => import('@/pages/sorcery/SorceryTenantFeatureFlagHistoryPage')
);
const SorceryGlobalFeatureFlagsPage = lazy(
  () => import('@/pages/sorcery/SorceryGlobalFeatureFlagsPage')
);
const SorceryAuditPage = lazy(() => import('@/pages/sorcery/SorceryAuditPage'));
const SorceryBillingPage = lazy(() => import('@/pages/sorcery/SorceryBillingPage'));
const SorceryIntegrationInterestPage = lazy(
  () => import('@/pages/sorcery/SorceryIntegrationInterestPage')
);
const SorceryFixtureCutoverPage = lazy(() => import('@/pages/sorcery/SorceryFixtureCutoverPage'));
const NextMyWorkGate = lazy(() =>
  import('@/features/my-work/NextMyWorkGate').then((m) => ({ default: m.NextMyWorkGate }))
);
const MyWorkWorkItemsPage = lazy(() => import('@/features/my-work/MyWorkWorkItemsPage'));
const MyWorkPullRequestsPage = lazy(() => import('@/features/my-work/MyWorkPullRequestsPage'));
const DevelopLayout = lazy(() => import('@/features/develop/DevelopLayout'));
const DevelopPhaseWorkItemsPage = lazy(
  () => import('@/features/develop/DevelopPhaseWorkItemsPage')
);
const GovernShellGate = lazy(() =>
  import('@/features/govern/GovernShellGate').then((m) => ({ default: m.GovernShellGate }))
);
const GovernLayout = lazy(() => import('@/features/govern/GovernLayout'));
const AdrIndexPage = lazy(() => import('@/features/govern/pages/AdrIndexPage'));
const AdrDetailPage = lazy(() => import('@/features/govern/pages/AdrDetailPage'));
const CodingStandardsIndexPage = lazy(
  () => import('@/features/govern/pages/CodingStandardsIndexPage')
);
const CodingStandardDetailPage = lazy(
  () => import('@/features/govern/pages/CodingStandardDetailPage')
);
const PolicyContextPage = lazy(() => import('@/features/govern/pages/PolicyContextPage'));
const AiControlsPage = lazy(() => import('@/features/govern/pages/AiControlsPage'));
const ExecutionPosturePage = lazy(() => import('@/features/govern/pages/ExecutionPosturePage'));
const AuditPosturePage = lazy(() => import('@/features/govern/pages/AuditPosturePage'));
const HybridByoPage = lazy(() => import('@/features/govern/pages/HybridByoPage'));
const RulesPage = lazy(() => import('@/features/govern/pages/RulesPage'));
const RepositoryProfilesPage = lazy(() => import('@/features/govern/pages/RepositoryProfilesPage'));
const InspectShellGate = lazy(() =>
  import('@/features/inspect/InspectShellGate').then((m) => ({ default: m.InspectShellGate }))
);
const InspectQualityPage = lazy(() => import('@/features/inspect/pages/QualityPage'));

export const AppRoutes = () => {
  const { t } = useTranslation('layout');

  return (
    <Suspense
      fallback={<div className="flex h-screen items-center justify-center">{t('loading')}</div>}
    >
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/mcp/authorize" element={<McpAuthorizePage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/request-access/:organizationId" element={<RequestAccessPage />} />
        <Route path="/request-access/work-item/:workItemId" element={<RequestAccessPage />} />
        <Route path="/choose-organization" element={<ChooseOrganizationPage />} />
        <Route path="/invite/:token" element={<InvitationLinkPage />} />
        {/* Microsoft admin-consent landing (spec 493 A-2b). Not flag-gated: the backend flag decides
            whether the callback runs, and a frontend/backend flag mismatch must not 404 here. */}
        <Route path="/connect/azure-devops/complete" element={<AzureDevOpsConsentCompletePage />} />

        {/* Protected Routes with Dashboard Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />

        {/* Aggregate Dashboards */}
        <Route
          path="/dashboards/teams"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <TeamsAggregateDashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/repositories"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <RepositoriesAggregateDashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/requirements"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <RequirementsDashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/requirements/authors/:email"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <AuthorDetailPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />

        {/* Repository Dashboard Routes - Table View (per spec FR-004) */}
        <Route
          path="/repositories"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <RepositoriesAggregateDashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        {/* Legacy route for backward compatibility */}
        <Route
          path="/dashboards/organization/:org_id/repositories"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <RepositoryDashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/work-items"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <WorkItemsPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-items/:id"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <WorkItemDetailPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inbox"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <InboxPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/my-work/work-items" element={<Navigate to="/develop/work-items" replace />} />
        <Route
          path="/my-work/pull-requests"
          element={<Navigate to="/develop/pull-requests" replace />}
        />
        <Route path="/my-work" element={<Navigate to="/develop/work-items" replace />} />
        <Route
          path="/develop"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <NextMyWorkGate>
                    <DevelopLayout />
                  </NextMyWorkGate>
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="work-items" replace />} />
          <Route path="work-items" element={<MyWorkWorkItemsPage />} />
          <Route path="work-items/:phase" element={<DevelopPhaseWorkItemsPage />} />
          <Route path="pull-requests" element={<MyWorkPullRequestsPage />} />
        </Route>
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <LeaderboardPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineers"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <EngineerDirectory />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineers/:id"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <EngineerReportCard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        {/* Teams routes */}
        {/* Teams main route - Aggregate Table View (per spec FR-002) */}
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <TeamsAggregateDashboard />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        {/* Teams management route for CRUD operations */}
        <Route
          path="/teams/manage"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <TeamsPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams/:teamId/dashboard"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <TeamDashboardPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        {/* Redirect /teams/:teamId to /teams/:teamId/dashboard */}
        <Route path="/teams/:teamId" element={<Navigate to="dashboard" replace />} />
        <Route
          path="/pull-requests"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <PullRequestHistoryPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/:prId"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <PrDetailView />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/govern"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <GovernShellGate />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          <Route element={<GovernLayout />}>
            <Route index element={<Navigate to="adrs" replace />} />
            <Route path="adrs" element={<AdrIndexPage />} />
            <Route path="adrs/:adrId" element={<AdrDetailPage />} />
            <Route path="repository-profiles" element={<RepositoryProfilesPage />} />
            <Route path="coding-standards" element={<CodingStandardsIndexPage />} />
            <Route path="coding-standards/:standardId" element={<CodingStandardDetailPage />} />
            <Route path="rules" element={<RulesPage />} />
            <Route path="rules/repository/:repositoryId" element={<ReviewStandardsPage />} />
            <Route path="policy-context" element={<PolicyContextPage />} />
            <Route path="ai-controls" element={<AiControlsPage />} />
            <Route path="execution-posture" element={<ExecutionPosturePage />} />
            <Route path="audit-posture" element={<AuditPosturePage />} />
            <Route path="hybrid-worker-byo" element={<HybridByoPage />} />
          </Route>
        </Route>
        <Route
          path="/govern/stack-profiles"
          element={<Navigate to="/govern/repository-profiles" replace />}
        />
        <Route
          path="/inspect/quality"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <InspectShellGate />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<InspectQualityPage />} />
        </Route>
        <Route path="/inspect/leaderboard" element={<Navigate to="/leaderboard" replace />} />
        <Route path="/inspect/engineers" element={<Navigate to="/engineers" replace />} />
        <Route path="/inspect/teams" element={<Navigate to="/teams" replace />} />
        <Route path="/inspect/repositories" element={<Navigate to="/repositories" replace />} />
        {/* Settings: shared layout + full-page outlet (nested routes) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <SettingsChrome>
                  <Settings />
                </SettingsChrome>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="repositories" element={<RepositoriesSettingsPage />} />
          <Route path="work-items" element={<WorkItemSettingsPage />} />
          <Route
            path="provider-connections"
            element={
              <NextShellSettingsGate>
                <ProviderConnectionsSettingsPage />
              </NextShellSettingsGate>
            }
          />
          <Route
            path="trigger-policy"
            element={
              <NextShellSettingsGate>
                <TriggerPolicySettingsPage />
              </NextShellSettingsGate>
            }
          />
          <Route
            path="context-freshness-policy"
            element={
              <NextShellSettingsGate>
                <ContextFreshnessPolicySettingsPage />
              </NextShellSettingsGate>
            }
          />
          <Route
            path="sdd-generation-gate-policy"
            element={
              <NextShellSettingsGate>
                <SddGenerationGatePolicySettingsPage />
              </NextShellSettingsGate>
            }
          />
          <Route
            path="citation-quality"
            element={
              <NextShellSettingsGate>
                <CitationQualitySettingsPage />
              </NextShellSettingsGate>
            }
          />
          <Route path="api-keys" element={<SettingsApiKeys />} />
          <Route path="billing" element={<SettingsBilling />} />
          <Route path="usage" element={<SettingsUsage />} />
          <Route path="api-guide" element={<SettingsApiGuide />} />
          <Route path="onboarding" element={<SettingsOnboardingPage />} />
          <Route path="organization" element={<OrganizationSettingsPage />} />
          <Route path="team" element={<OrganizationMembers />} />
          <Route path="git" element={<GitIntegrationsPage />} />
          {/* Legacy per-provider routes — still the OAuth returnTo targets, so they must
              preserve the incoming query string when redirecting. */}
          <Route
            path="github/register"
            element={<LegacyGitSettingsRedirect platform="github-app" />}
          />
          <Route path="github/success" element={<GitHubAppSuccess />} />
          <Route path="github/error" element={<GitHubAppError />} />
          <Route
            path="mcp"
            element={
              <NextShellSettingsGate>
                <MemberMcpIntegrationPage />
              </NextShellSettingsGate>
            }
          />
          <Route
            path="gitlab/configuration"
            element={<LegacyGitSettingsRedirect platform="gitlab" extra={{ section: 'byoc' }} />}
          />
          <Route path="gitlab" element={<LegacyGitSettingsRedirect platform="gitlab" />} />
          <Route path="gitea" element={<LegacyGitSettingsRedirect platform="gitea" />} />
        </Route>

        <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />

        {/* Organization Routes */}
        <Route path="/organization/:organizationId" element={<Navigate to="members" replace />} />
        <Route
          path="/organization/:organizationId/members"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <OrganizationMembers />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/:organizationId/teams"
          element={<Navigate to="/teams/manage" replace />}
        />
        <Route
          path="/organization/:organizationId/audit-logs"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <AuditLogsPage />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/:organizationId/notifications"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout>
                  <UserNotifications />
                </DashboardLayout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/:organizationId/usage"
          element={<Navigate to="/settings/usage" replace />}
        />

        {/* Onboarding Routes */}
        {onboardingRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<ProtectedRoute>{route.element}</ProtectedRoute>}
          />
        ))}

        <Route
          path="/style-guide"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <StyleGuide />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy redirect: /dashboard -> / */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />

        {/* Sorcery internal admin (platform allowlist); single auth gate for all child routes */}
        <Route
          path="/sorcery"
          element={
            <ProtectedRoute>
              <InternalAdminGuard>
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              </InternalAdminGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<SorceryTenantsPage />} />
          <Route path="feature-flags/global" element={<SorceryGlobalFeatureFlagsPage />} />
          <Route path="tenants/:organizationId" element={<SorceryTenantDetailPage />} />
          <Route
            path="tenants/:organizationId/feature-flags"
            element={<SorceryTenantFeatureFlagsPage />}
          />
          <Route
            path="tenants/:organizationId/feature-flags/history"
            element={<SorceryTenantFeatureFlagHistoryPage />}
          />
          <Route path="audit" element={<SorceryAuditPage />} />
          <Route path="fixture-cutover" element={<SorceryFixtureCutoverPage />} />
          <Route path="billing" element={<SorceryBillingPage />} />
          <Route path="integrations" element={<SorceryIntegrationInterestPage />} />
        </Route>

        {/* Account lifecycle pages (public — user may be logged out) */}
        <Route path="/account/suspended" element={<AccountSuspendedPage />} />

        {/* 404 Route */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
